import type { ScaleBand } from 'd3-scale';
import { isNil } from 'lodash-es';

import type { BarScaleLinear, BarScaleTime } from '../../types/guards.js';
import type {
  AnyXYChartScale,
  MapDatapoints,
  XYChartAxisDirection,
} from '../../types/xy-chart-internals.js';
import type { BarDatapoint } from '../../types/xy-chart.js';
import {
  isCategoricalBarScale,
  isNumericalBarScale,
  isTimeBarScale,
} from '../../utils/type-guards.js';

export const applyNumericalBarScaleTransformations = (
  args: BarScaleLinear,
  accessorCoord: 'x' | 'y',
  baseline: XYChartAxisDirection,
  relAccessor?: ((value: number) => number) | undefined,
  accumulator?: number,
  binDomain?: number,
) => {
  const isNotBaseline = baseline !== undefined && accessorCoord !== baseline;
  let { v0, v1 } = args;

  if (isNotBaseline && accumulator !== undefined) {
    v0 = !isNil(v0) ? v0 + accumulator : accumulator;
    v1 = !isNil(v1) ? v1 + accumulator : accumulator;
  }

  if (relAccessor && isNotBaseline && !isNil(binDomain)) {
    return {
      v0: !isNil(v0) ? (v0 / binDomain) * 100 : v0,
      v1: !isNil(v1) ? (v1 / binDomain) * 100 : v1,
      scale: args.scale,
    };
  }

  if (relAccessor) {
    return {
      v0: !isNil(v0) ? relAccessor(v0) : v0,
      v1: !isNil(v1) ? relAccessor(v1) : v1,
      scale: args.scale,
    };
  }

  return { v0, v1, scale: args.scale };
};

function calculateBarOrigin(
  args: BarScaleLinear | BarScaleTime,
  accessorCoord: Exclude<XYChartAxisDirection, undefined>,
) {
  const { scale, v0, v1 } = args;
  if (!isNil(v0) && !isNil(v1)) {
    return Math.min(scale(v0), scale(v1));
  }
  if (!isNil(v1)) {
    if (typeof v1 === 'number' && v1 < 0) {
      return accessorCoord === 'x' ? scale(v1) : scale(0);
    }
    return accessorCoord === 'x'
      ? typeof v1 === 'number'
        ? scale(0)
        : scale(scale.invert(0))
      : scale(v1);
  }
  if (!isNil(v0)) {
    return scale(v0);
  }
}

function genericAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  accessorCoord: Exclude<XYChartAxisDirection, undefined>,
  dimensionScale?: ScaleBand<string>,
  seriesId?: string,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: BarDatapoint) => {
    const v0 = accessorCoord === 'x' ? datapoint.x0 : datapoint.y0;
    const v1 = accessorCoord === 'x' ? datapoint.x1 : datapoint.y1;
    let args = { scale, v0, v1 };
    const { baseline, accumulator, binDomain } =
      datapointsMetadata.get(datapoint) ?? {};

    if (isCategoricalBarScale(args)) {
      if (!isNil(args.v0)) {
        if (dimensionScale && seriesId) {
          return (args.scale(args.v0) ?? 0) + (dimensionScale(seriesId) ?? 0);
        }
        return args.scale(args.v0) ?? 0;
      }
      if (!isNil(args.v1)) {
        if (accessorCoord === 'x') {
          const [min] = args.scale.range();
          return min;
        } else {
          const catWidth = Math.floor(args.scale.bandwidth() / 2);
          return (args.scale(args.v1) ?? 0) + catWidth;
        }
      }
    }

    if (isNumericalBarScale(args)) {
      args = applyNumericalBarScaleTransformations(
        args,
        accessorCoord,
        baseline,
        relAccessor,
        accumulator,
        binDomain,
      );
    }

    if (isNumericalBarScale(args) || isTimeBarScale(args)) {
      const barOrigin = calculateBarOrigin(args, accessorCoord);
      if (!isNil(barOrigin)) {
        return barOrigin;
      }
    }

    throw Error(`unknown ${accessorCoord}Accessor value for Bar`);
  };
}

export function xAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  dimensionScale?: ScaleBand<string>,
  seriesId?: string,
  relAccessor?: (value: number) => number,
) {
  return genericAccessor(
    scale,
    datapointsMetadata,
    'x',
    dimensionScale,
    seriesId,
    relAccessor,
  );
}

export function yAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  dimensionScale?: ScaleBand<string>,
  seriesId?: string,
  relAccessor?: (value: number) => number,
) {
  return genericAccessor(
    scale,
    datapointsMetadata,
    'y',
    dimensionScale,
    seriesId,
    relAccessor,
  );
}

// Just for Numerical and Time scales
const calculateBarDimension = (args: BarScaleLinear | BarScaleTime) => {
  const { scale, v0, v1 } = args;
  if (!isNil(v0) && !isNil(v1)) {
    return Math.abs(scale(v1) - scale(v0));
  }
  if (!isNil(v1)) {
    const zero =
      typeof v1 === 'number' ? scale(0) : scale(scale.invert(scale.range()[0]));

    return Math.abs(zero - scale(v1));
  }
  if (!isNil(v0)) {
    return scale(v0);
  }
  return undefined;
};

function spanAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  accessorCoord: Exclude<XYChartAxisDirection, undefined>,
  dimensionScale?: ScaleBand<string>,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: BarDatapoint) => {
    const v0 = accessorCoord === 'x' ? datapoint.x0 : datapoint.y0;
    const v1 = accessorCoord === 'x' ? datapoint.x1 : datapoint.y1;
    let args = { scale, v0, v1 };
    const { baseline, accumulator, binDomain } =
      datapointsMetadata.get(datapoint) ?? {};

    if (isCategoricalBarScale(args)) {
      if (!isNil(args.v0)) {
        if (dimensionScale) {
          return dimensionScale.bandwidth();
        }
        return args.scale.bandwidth();
      }
      if (!isNil(args.v1)) {
        if (accessorCoord === 'x') {
          const catWidth = Math.floor(args.scale.bandwidth() / 2);
          return (args.scale(args.v1) ?? 0) + catWidth;
        } else {
          const [, min] = scale.range();
          const catWidth = Math.floor(args.scale.bandwidth() / 2);
          const barY = (args.scale(args.v1) ?? 0) + catWidth;
          return min - barY;
        }
      }
    }

    if (isNumericalBarScale(args)) {
      args = applyNumericalBarScaleTransformations(
        args,
        accessorCoord,
        baseline,
        relAccessor,
        accumulator,
        binDomain,
      );
    }

    if (isNumericalBarScale(args) || isTimeBarScale(args)) {
      const span = calculateBarDimension(args);
      if (!isNil(span)) {
        return span;
      }
    }

    throw Error(
      `unknown ${
        accessorCoord === 'x' ? 'width' : 'height'
      }Accessor value for Bar`,
    );
  };
}

export function widthAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  dimensionScale?: ScaleBand<string>,
  relAccessor?: (value: number) => number,
) {
  return spanAccessor(
    scale,
    datapointsMetadata,
    'x',
    dimensionScale,
    relAccessor,
  );
}

export function heightAccessor(
  scale: AnyXYChartScale,
  datapointsMetadata: MapDatapoints,
  dimensionScale?: ScaleBand<string>,
  relAccessor?: (value: number) => number,
) {
  return spanAccessor(
    scale,
    datapointsMetadata,
    'y',
    dimensionScale,
    relAccessor,
  );
}
